import localForage from 'localforage';
import _ from '@lodash';
import { persistReducer } from 'redux-persist';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'app/store';
import { type UsersState, type UsersActionTypes } from 'app/store/types';

const initialState: UsersState = {};

const usersReducer = (state = initialState, action: UsersActionTypes) => {
	switch (action.type) {
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				licenseGroupId,
				data: { users, pendingUsers },
			} = action.payload;

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					...(users && { byId: users }),
					...(pendingUsers && { pendingById: pendingUsers }),
				},
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'users',
		storage: localForage,
		whitelist: localStorage.getItem('persist') ? undefined : [],
	},
	usersReducer,
);

// Selectors
const getUsersByIdIncludingHidden = ({ app: { selectedLicenseGroupId }, users }: RootState) =>
	users[selectedLicenseGroupId].byId;

const getPendingUsersByIdIncludingHidden = ({ app: { selectedLicenseGroupId }, users }: RootState) =>
	users[selectedLicenseGroupId].pendingById;

export const getAllUsersByIdIncludingHidden = createSelector(
	[getUsersByIdIncludingHidden, getPendingUsersByIdIncludingHidden],
	(usersByIdIncludingHidden, pendingUsersByIdIncludingHidden) => ({
		...usersByIdIncludingHidden,
		...pendingUsersByIdIncludingHidden,
	}),
);

export const getAllUsersIncludingHidden = createSelector(
	[getAllUsersByIdIncludingHidden],
	(allUsersByIdIncludingHidden) => Object.values(allUsersByIdIncludingHidden),
);

export const getUsersAndHiddenById = createSelector([getUsersByIdIncludingHidden], (usersById) => usersById);

export const getUsersById = createSelector([getUsersByIdIncludingHidden], (usersById) =>
	_.pickBy(usersById, ({ hidden }) => !hidden),
);

export const getUsers = createSelector([getUsersById], (usersById) => Object.values(usersById));

export const getPendingUsersById = createSelector([getPendingUsersByIdIncludingHidden], (pendingUsersById) =>
	_.pickBy(pendingUsersById, ({ hidden }) => !hidden),
);

export const getAllUsersById = createSelector([getAllUsersByIdIncludingHidden], (allUsersByIdIncludingHidden) =>
	_.pickBy(allUsersByIdIncludingHidden, ({ hidden }) => !hidden),
);

export const getAllUsers = createSelector([getAllUsersById], (allUsersById) => Object.values(allUsersById));
